export interface FirebaseRuntimeConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
  messagingSenderId?: string;
}

export interface LegionRuntimeConfig {
  firebase?: FirebaseRuntimeConfig;
}

function normalizeOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function validateLegionRuntimeConfig(raw: unknown): {
  config: LegionRuntimeConfig;
  error: string | null;
} {
  if (raw == null) {
    return { config: {}, error: null };
  }

  if (!isRecord(raw)) {
    return {
      config: {},
      error: "Invalid runtime-config payload. Expected a JSON object.",
    };
  }

  const rawFirebase = raw.firebase;
  if (rawFirebase !== undefined && !isRecord(rawFirebase)) {
    return {
      config: {},
      error:
        "Invalid runtime-config payload. The firebase section must be an object when provided.",
    };
  }

  const firebase: FirebaseRuntimeConfig | undefined = rawFirebase
    ? {
        apiKey: normalizeOptionalString(rawFirebase.apiKey),
        authDomain: normalizeOptionalString(rawFirebase.authDomain),
        projectId: normalizeOptionalString(rawFirebase.projectId),
        appId: normalizeOptionalString(rawFirebase.appId),
        messagingSenderId: normalizeOptionalString(rawFirebase.messagingSenderId),
      }
    : undefined;

  const hasAnyFirebaseField = Boolean(
    firebase?.apiKey ||
      firebase?.authDomain ||
      firebase?.projectId ||
      firebase?.appId ||
      firebase?.messagingSenderId
  );

  if (!hasAnyFirebaseField) {
    return { config: firebase ? { firebase } : {}, error: null };
  }

  const missingFields = ["apiKey", "projectId"].filter(
    (field) => !firebase?.[field as keyof FirebaseRuntimeConfig]
  );

  if (missingFields.length > 0) {
    return {
      config: { firebase },
      error: `Firebase runtime config is incomplete. Missing: ${missingFields.join(", ")}.`,
    };
  }

  return { config: { firebase }, error: null };
}
